/**
 * TierTemplatesDrawer - Right-side drawer for editing the three pricing tier
 * templates of a proposal template: tier name, description, advantages,
 * default line items, and the recommended flag. Tier labels come from the org's
 * settings, so this shows "Essential" rather than "Good" where renamed.
 *
 * Edits a local draft copy; nothing persists until "Save Tier Templates".
 */

import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  CaretDown,
  CaretUp,
  ChartBar,
  Check,
  DotsSixVertical,
  FileText,
  Info,
  Plus as Add,
  Star,
  Trash as Delete,
  X as Close,
} from '@phosphor-icons/react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useTierLabels } from '../../hooks/useTierLabels';
import type {
  ProposalTemplateDefaultLineItem,
  ProposalTemplateDefaultTier,
  TierLabelMap,
  TierType,
} from '../../types/proposals';

const TIER_COLORS: Record<TierType, 'warning' | 'primary' | 'success'> = {
  good: 'warning',
  better: 'primary',
  best: 'success',
};

interface TierTemplatesDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Display label of the proposal type these tiers belong to, e.g. "Residential" */
  proposalTypeLabel: string;
  tiers: ProposalTemplateDefaultTier[];
  saving: boolean;
  onSave: (tiers: ProposalTemplateDefaultTier[]) => void;
}

// The API serializes decimals as strings ("25.00") - normalize to numbers so
// the inputs display cleanly and edits stay numeric.
const cloneTiers = (tiers: ProposalTemplateDefaultTier[]): ProposalTemplateDefaultTier[] =>
  tiers.map((tier) => ({
    ...tier,
    advantages: [...tier.advantages],
    default_line_items: tier.default_line_items.map((item) => ({
      ...item,
      quantity: Number(item.quantity) || 0,
      unit_price: Number(item.unit_price) || 0,
    })),
  }));

const countLabel = (count: number, noun: string) =>
  `${count === 0 ? 'no' : count} ${noun}${count === 1 ? '' : 's'}`;

interface TierTemplateCardProps {
  tier: ProposalTemplateDefaultTier;
  expanded: boolean;
  isMobile: boolean;
  proposalTypeLabel: string;
  tierLabels: TierLabelMap;
  onToggleExpand: () => void;
  onToggleRecommended: () => void;
  onChange: (patch: Partial<ProposalTemplateDefaultTier>) => void;
}

function TierTemplateCard({
  tier,
  expanded,
  isMobile,
  proposalTypeLabel,
  tierLabels,
  onToggleExpand,
  onToggleRecommended,
  onChange,
}: TierTemplateCardProps) {
  const meta = {
    label: tierLabels[tier.tier_type] ?? tier.name ?? tier.tier_type,
    color: TIER_COLORS[tier.tier_type] ?? ('primary' as const),
  };
  const advantageCount = tier.advantages.filter((a) => a.trim()).length;
  const lineItemCount = tier.default_line_items.length;

  const handleAdvantageChange = (index: number, value: string) => {
    const advantages = [...tier.advantages];
    advantages[index] = value;
    onChange({ advantages });
  };

  const handleRemoveAdvantage = (index: number) => {
    onChange({ advantages: tier.advantages.filter((_, i) => i !== index) });
  };

  const handleLineItemChange = (
    index: number,
    field: keyof ProposalTemplateDefaultLineItem,
    value: string | number
  ) => {
    const items = [...tier.default_line_items];
    items[index] = { ...items[index], [field]: value };
    onChange({ default_line_items: items });
  };

  const handleAddLineItem = () => {
    const newItem: ProposalTemplateDefaultLineItem = {
      description: '',
      quantity: 1,
      unit: 'ea',
      unit_price: 0,
      sort_order: tier.default_line_items.length,
    };
    onChange({ default_line_items: [...tier.default_line_items, newItem] });
  };

  const handleRemoveLineItem = (index: number) => {
    onChange({ default_line_items: tier.default_line_items.filter((_, i) => i !== index) });
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: 'lg',
        overflow: 'hidden',
        borderLeft: '4px solid',
        borderLeftColor: `${meta.color}.solidBg`,
        bgcolor: expanded ? `${meta.color}.softBg` : 'background.surface',
        transition: 'background-color 0.15s ease',
      }}
    >
      {/* Card header - click to expand/collapse */}
      <Box
        onClick={onToggleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.25 },
          p: { xs: 1.5, sm: 2 },
          cursor: 'pointer',
          '&:hover': expanded ? undefined : { bgcolor: 'background.level1' },
        }}
      >
        <Box
          component="span"
          sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'text.tertiary', flexShrink: 0 }}
        >
          <DotsSixVertical size={18} />
        </Box>
        <Chip
          size="sm"
          variant="soft"
          color={meta.color}
          startDecorator={
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: `${meta.color}.solidBg` }} />
          }
          sx={{ textTransform: 'uppercase', fontWeight: 'lg', letterSpacing: '0.5px', flexShrink: 0 }}
        >
          {meta.label}
        </Chip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-md" noWrap>
            {tier.name || meta.label}
          </Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
            {countLabel(advantageCount, 'advantage')} · {countLabel(lineItemCount, 'line item')}
          </Typography>
        </Box>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          aria-label={tier.is_recommended ? 'Remove recommended flag' : 'Mark as recommended tier'}
          title={tier.is_recommended ? 'Recommended tier' : 'Mark as recommended'}
          onClick={(event) => {
            event.stopPropagation();
            onToggleRecommended();
          }}
          sx={{ color: tier.is_recommended ? 'warning.400' : 'text.tertiary', flexShrink: 0 }}
        >
          <Star size={18} weight={tier.is_recommended ? 'fill' : 'regular'} />
        </IconButton>
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          aria-label={expanded ? `Collapse ${meta.label} tier` : `Expand ${meta.label} tier`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand();
          }}
          sx={{ borderRadius: '50%', bgcolor: 'background.surface', flexShrink: 0 }}
        >
          {expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </IconButton>
      </Box>

      {expanded && (
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={2}>
            {/* Name + description */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 220px) minmax(0, 1fr)' },
                gap: 1.5,
              }}
            >
              <FormControl size="sm">
                <FormLabel>Tier Name</FormLabel>
                <Input
                  value={tier.name}
                  onChange={(e) => onChange({ name: e.target.value })}
                  placeholder={meta.label}
                />
              </FormControl>
              <FormControl size="sm">
                <FormLabel>Description</FormLabel>
                <Textarea
                  minRows={1}
                  value={tier.description}
                  onChange={(e) => onChange({ description: e.target.value })}
                  placeholder="Brief description of this tier option"
                />
              </FormControl>
            </Box>

            {/* Advantages */}
            <Box>
              <Stack
                direction="row"
                sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}
              >
                <Typography level="title-sm">Advantages</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Shown as bullets on the proposal
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {tier.advantages.map((advantage, index) => (
                  <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Sheet
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.surface',
                        fontSize: 'xs',
                        color: 'text.secondary',
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Sheet>
                    <Input
                      size="sm"
                      value={advantage}
                      onChange={(e) => handleAdvantageChange(index, e.target.value)}
                      placeholder="e.g., Manufacturer-backed 25-year shingle warranty"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      aria-label="Remove advantage"
                      onClick={() => handleRemoveAdvantage(index)}
                    >
                      <Close size={16} />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="sm"
                variant="plain"
                color="primary"
                startDecorator={<Add size={16} />}
                onClick={() => onChange({ advantages: [...tier.advantages, ''] })}
                sx={{ mt: tier.advantages.length > 0 ? 1 : 0 }}
              >
                Add advantage
              </Button>
            </Box>

            {/* Default line items */}
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Default Line Items
              </Typography>
              {tier.default_line_items.length === 0 ? (
                <Sheet
                  variant="outlined"
                  sx={{
                    borderStyle: 'dashed',
                    borderRadius: 'lg',
                    bgcolor: 'background.surface',
                    p: { xs: 2.5, sm: 4 },
                    textAlign: 'center',
                  }}
                >
                  <Sheet
                    variant="soft"
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      mx: 'auto',
                      mb: 1.5,
                      color: 'text.tertiary',
                    }}
                  >
                    <FileText size={22} />
                  </Sheet>
                  <Typography level="title-sm" sx={{ mb: 0.5 }}>
                    No default line items
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary', maxWidth: 420, mx: 'auto' }}>
                    Anything you add here drops into every new {proposalTypeLabel.toLowerCase()} proposal
                    at this tier, priced and ready for the rep to adjust.
                  </Typography>
                </Sheet>
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {tier.default_line_items.map((item, index) => (
                    <Sheet
                      key={index}
                      variant="outlined"
                      sx={{ borderRadius: 'md', bgcolor: 'background.surface', p: 1.5 }}
                    >
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          <Typography level="title-sm" noWrap sx={{ flex: 1, minWidth: 0 }}>
                            {item.description || `Line item ${index + 1}`}
                          </Typography>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            aria-label="Remove line item"
                            onClick={() => handleRemoveLineItem(index)}
                          >
                            <Delete size={18} />
                          </IconButton>
                        </Box>
                        <FormControl size="sm">
                          <FormLabel>Description</FormLabel>
                          <Input
                            value={item.description}
                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                            placeholder="Line item description"
                          />
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 1, '& > *': { minWidth: 0 }, '& input': { minWidth: 0 } }}>
                          <FormControl size="sm" sx={{ flex: 1 }}>
                            <FormLabel>Qty</FormLabel>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) =>
                                handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                          <FormControl size="sm" sx={{ flex: 1 }}>
                            <FormLabel>Unit</FormLabel>
                            <Input
                              value={item.unit}
                              onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                            />
                          </FormControl>
                          <FormControl size="sm" sx={{ flex: 1 }}>
                            <FormLabel>Unit Price</FormLabel>
                            <Input
                              type="number"
                              startDecorator="$"
                              value={item.unit_price}
                              onChange={(e) =>
                                handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                        </Box>
                      </Stack>
                    </Sheet>
                  ))}
                </Stack>
              ) : (
                <Sheet
                  variant="outlined"
                  sx={{ borderRadius: 'md', overflow: 'hidden', bgcolor: 'background.surface' }}
                >
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--joy-palette-background-level1)' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px' }}>Description</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '12px', width: '88px' }}>Qty</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '12px', width: '76px' }}>Unit</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', width: '118px' }}>Unit Price</th>
                          <th style={{ padding: '8px 12px', width: '44px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {tier.default_line_items.map((item, index) => (
                          <tr key={index} style={{ borderTop: '1px solid var(--joy-palette-divider)' }}>
                            <td style={{ padding: '4px 8px' }}>
                              <Input
                                size="sm"
                                variant="plain"
                                value={item.description}
                                onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                                placeholder="Line item description"
                                sx={{ width: '100%', minWidth: 0, '& input': { minWidth: 0 }, '--Input-focusedHighlight': 'transparent' }}
                              />
                            </td>
                            <td style={{ padding: '4px 8px' }}>
                              <Input
                                size="sm"
                                variant="plain"
                                type="number"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)
                                }
                                sx={{ width: '100%', minWidth: 0, '& input': { minWidth: 0 }, textAlign: 'center', '--Input-focusedHighlight': 'transparent' }}
                              />
                            </td>
                            <td style={{ padding: '4px 8px' }}>
                              <Input
                                size="sm"
                                variant="plain"
                                value={item.unit}
                                onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                                sx={{ width: '100%', minWidth: 0, '& input': { minWidth: 0 }, textAlign: 'center', '--Input-focusedHighlight': 'transparent' }}
                              />
                            </td>
                            <td style={{ padding: '4px 8px' }}>
                              <Input
                                size="sm"
                                variant="plain"
                                type="number"
                                value={item.unit_price}
                                onChange={(e) =>
                                  handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)
                                }
                                startDecorator="$"
                                sx={{ width: '100%', minWidth: 0, '& input': { minWidth: 0 }, textAlign: 'right', '--Input-focusedHighlight': 'transparent' }}
                              />
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                aria-label="Remove line item"
                                onClick={() => handleRemoveLineItem(index)}
                              >
                                <Delete size={18} />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Sheet>
              )}
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<Add size={16} />}
                onClick={handleAddLineItem}
                sx={{ mt: 1.5, borderStyle: 'dashed', bgcolor: 'background.surface' }}
              >
                Add Line Item
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Sheet>
  );
}

export default function TierTemplatesDrawer({
  open,
  onClose,
  proposalTypeLabel,
  tiers,
  saving,
  onSave,
}: TierTemplatesDrawerProps) {
  const isMobile = useIsMobile();
  const { labels: tierLabels } = useTierLabels();
  const [draft, setDraft] = useState<ProposalTemplateDefaultTier[]>([]);
  const [dirty, setDirty] = useState(false);
  const [expandedTier, setExpandedTier] = useState<TierType | null>('good');
  const prevOpenRef = useRef(false);

  // Re-seed the draft each time the drawer opens
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setDraft(cloneTiers(tiers));
      setDirty(false);
      setExpandedTier('good');
    }
    prevOpenRef.current = open;
  }, [open, tiers]);

  const updateTier = (tierType: TierType, patch: Partial<ProposalTemplateDefaultTier>) => {
    setDraft((prev) => prev.map((t) => (t.tier_type === tierType ? { ...t, ...patch } : t)));
    setDirty(true);
  };

  const toggleRecommended = (tierType: TierType) => {
    setDraft((prev) =>
      prev.map((t) => ({ ...t, is_recommended: t.tier_type === tierType ? !t.is_recommended : false }))
    );
    setDirty(true);
  };

  const handleClose = () => {
    if (saving) return;
    if (dirty && !confirm('Discard unsaved tier template changes?')) return;
    onClose();
  };

  const handleSave = () => {
    onSave(
      draft.map((tier) => ({
        ...tier,
        advantages: tier.advantages.map((a) => a.trim()).filter(Boolean),
      }))
    );
  };

  const sortedTiers = [...draft].sort((a, b) => a.sort_order - b.sort_order);
  const totalLineItems = draft.reduce((sum, tier) => sum + tier.default_line_items.length, 0);
  const recommendedTier = draft.find((tier) => tier.is_recommended);
  const recommendedName = recommendedTier
    ? recommendedTier.name || tierLabels[recommendedTier.tier_type] || recommendedTier.tier_type
    : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        content: {
          sx: {
            width: { xs: '100%', sm: 640, md: 760 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Sheet
          sx={{
            width: 40,
            height: 40,
            borderRadius: 'md',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            background: 'linear-gradient(135deg, var(--joy-palette-primary-400), var(--joy-palette-primary-600))',
            color: '#fff',
          }}
        >
          <ChartBar size={20} weight="fill" />
        </Sheet>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-lg">Tier Templates</Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Defaults applied to every new{' '}
            <Typography component="span" sx={{ color: 'primary.plainColor', fontWeight: 'lg' }}>
              {proposalTypeLabel}
            </Typography>{' '}
            proposal
          </Typography>
        </Box>
        <IconButton size="sm" variant="plain" color="neutral" aria-label="Close" onClick={handleClose}>
          <Close size={18} />
        </IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 1.5, sm: 2 } }}>
        <Stack spacing={1.5}>
          <Alert variant="soft" color="primary" startDecorator={<Info size={18} />} sx={{ borderRadius: 'lg' }}>
            <Typography level="body-sm" sx={{ color: 'inherit' }}>
              Reps can override any tier on a single proposal. Changes here apply to proposals created
              from now on.
            </Typography>
          </Alert>

          {sortedTiers.map((tier) => (
            <TierTemplateCard
              key={tier.tier_type}
              tier={tier}
              expanded={expandedTier === tier.tier_type}
              isMobile={isMobile}
              proposalTypeLabel={proposalTypeLabel}
              tierLabels={tierLabels}
              onToggleExpand={() =>
                setExpandedTier((current) => (current === tier.tier_type ? null : tier.tier_type))
              }
              onToggleRecommended={() => toggleRecommended(tier.tier_type)}
              onChange={(patch) => updateTier(tier.tier_type, patch)}
            />
          ))}
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.surface',
        }}
      >
        <Typography level="body-sm" sx={{ color: 'text.secondary', flex: '1 1 auto', minWidth: 0 }}>
          {draft.length} tiers · {totalLineItems} line item{totalLineItems === 1 ? '' : 's'} ·{' '}
          {recommendedName ? `${recommendedName} recommended` : 'no tier recommended'}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ ml: 'auto', width: { xs: '100%', sm: 'auto' }, '& > button': { flex: { xs: 1, sm: '0 0 auto' } } }}
        >
          <Button variant="outlined" color="neutral" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            startDecorator={<Check size={16} />}
            onClick={handleSave}
            loading={saving}
          >
            Save Tier Templates
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
